import type { CoteraDuckDB } from '../db';
import { duckDbIdentifier, duckDbStringLiteral } from './sql';
import { createDuckDbWorkQueue } from './work-queue';

/** What a write needs from the database. */
export type DuckDbWriteTarget = Pick<CoteraDuckDB, 'rawQuery' | 'replaceFile'>;

/**
 * Where a table's contents come from.
 *
 * `query` derives the schema as well as the rows, which is how a parquet is
 * loaded. `rows` needs the schema to already exist, and goes through
 * `read_json_auto` over a registered file rather than a generated `VALUES`
 * list — a payload can carry tens of thousands of records, and a SQL string
 * that large is slow to build and slower to parse.
 */
export type DuckDbTableSource =
  | { readonly from: 'query'; readonly sql: string }
  | {
      readonly from: 'rows';
      readonly rows: readonly unknown[];
      /** Table columns being written, in order. */
      readonly columns: readonly string[];
      /** Expressions selected from the JSON, matching `columns` in order. */
      readonly select: readonly string[];
      /** Optional filter on the JSON rows, without the `WHERE`. */
      readonly where?: string;
    };

/**
 * The unserialized write vocabulary, handed to a {@link DuckDbTable.write}
 * callback. Everything here runs inside that call's lock.
 */
export type DuckDbTableWriter = {
  /** Creates the table if it is not already there. Declared schemas only. */
  readonly ensure: () => Promise<void>;
  /** Replaces the whole table. */
  readonly replace: (source: DuckDbTableSource) => Promise<void>;
  /**
   * Appends `source` to an already-existing table.
   *
   * `slot` names the registered file a `rows` source reuses — see
   * {@link DuckDbTableSource}. Callers writing the whole table pass a constant;
   * `DuckDbPartitionedTable` derives one from the partition key.
   */
  readonly insertFrom: (
    source: DuckDbTableSource,
    slot: string
  ) => Promise<void>;
  readonly deleteWhere: (where: string) => Promise<void>;
  readonly insert: (params: {
    columns: readonly string[];
    /** Value expressions, matching `columns` in order. */
    values: readonly string[];
  }) => Promise<void>;
  /** Replaces the rows matching `where` with one new row. */
  readonly upsert: (params: {
    where: string;
    columns: readonly string[];
    values: readonly string[];
  }) => Promise<void>;
  /** Anything the vocabulary above does not cover, inside the same lock. */
  readonly exec: (sql: string) => Promise<void>;
};

/**
 * A table in the browser's DuckDB.
 *
 * Deliberately one type for every table, whatever a given query happens to do
 * with it. A table that is joined today can be the `FROM` tomorrow, and naming
 * it for its role in one query — a "side table" — put that role in the type
 * system where it does not belong. What actually differs between tables is how
 * they are *populated*: from a query, which derives the schema, or from rows
 * into a schema declared up front, optionally one partition at a time. That is
 * {@link DuckDbTableSource}, not a different kind of table.
 *
 * Always a real table, never `CREATE TEMP TABLE`: `rawQuery` takes an
 * arbitrary connection from a pool, so a temp table is invisible to the next
 * read and surfaces as "Table … does not exist" from a different connection.
 *
 * Every write is serialized against other writes to the same table. Two
 * overlapping runs otherwise interleave — a `CREATE OR REPLACE` landing in the
 * middle of another run's replay, or a partition reload swallowing a
 * single-row update that arrived between its delete and its insert.
 */
export class DuckDbTable {
  readonly name: string;
  /** The quoted identifier, for interpolating into a query. */
  readonly sql: string;
  /**
   * Column definitions, e.g. `dataset_id TEXT NOT NULL`. Undefined for a table
   * whose schema is derived by the query that fills it.
   */
  protected readonly columnDefs: readonly string[] | undefined;

  constructor(params: { name: string; columns?: readonly string[] }) {
    this.name = params.name;
    this.sql = duckDbIdentifier(params.name);
    this.columnDefs = params.columns;
  }

  /**
   * Runs several writes under one lock.
   *
   * Use the writer it hands you, not this table's own methods — those take the
   * lock again and would wait on a call that has not finished.
   *
   * `shouldRun` is checked once the queued run reaches the front, so a
   * superseded run does no work. Use it only where a later run fully replaces
   * this one; never for an increment a later run would not redo.
   */
  write<T>(
    db: DuckDbWriteTarget,
    run: (writer: DuckDbTableWriter) => Promise<T>,
    options: { shouldRun?: () => boolean } = {}
  ): Promise<T | undefined> {
    const shouldRun = options.shouldRun;
    return duckDbTableWrites.run(this.name, async () => {
      if (shouldRun !== undefined && !shouldRun()) {
        return undefined;
      }
      return await run(this.createWriter(db));
    });
  }

  async ensure(db: DuckDbWriteTarget): Promise<void> {
    await this.write(db, (writer) => writer.ensure());
  }

  async replace(
    db: DuckDbWriteTarget,
    source: DuckDbTableSource
  ): Promise<void> {
    await this.write(db, (writer) => writer.replace(source));
  }

  async upsert(
    db: DuckDbWriteTarget,
    params: {
      where: string;
      columns: readonly string[];
      values: readonly string[];
    }
  ): Promise<void> {
    await this.write(db, (writer) => writer.upsert(params));
  }

  /**
   * The unserialized write vocabulary, bound to one database.
   *
   * Deliberately a closure rather than a set of methods: the statements below
   * are the substance of this module and nothing about the class shape changes
   * them.
   */
  protected createWriter(db: DuckDbWriteTarget): DuckDbTableWriter {
    const table = this.name;
    const columnDefs = this.columnDefs;

    const exec = async (sql: string): Promise<void> => {
      await db.rawQuery(sql);
    };

    const ensure = async (): Promise<void> => {
      if (columnDefs === undefined) {
        return;
      }
      await exec(
        `CREATE TABLE IF NOT EXISTS ${table} (${columnDefs.join(', ')})`
      );
    };

    /**
     * Fills an already-existing table from `source`, appending.
     *
     * `slot` names the registered file this reuses. Derived from what is being
     * written rather than freshly minted, so `replaceFile` can drop the
     * previous registration — a fresh name every call leaks a buffer into the
     * wasm filesystem per reload, and these reload on a timer.
     */
    const append = async (
      source: DuckDbTableSource,
      slot: string
    ): Promise<void> => {
      if (source.from === 'query') {
        await exec(`INSERT INTO ${table} ${source.sql}`);
        return;
      }
      if (source.rows.length === 0) {
        return;
      }
      const fileName = `${table}-${slot}.json`;
      await db.replaceFile(
        fileName,
        new File([JSON.stringify(source.rows)], fileName, {
          type: 'application/json',
        })
      );
      await exec(
        `INSERT INTO ${table} (${source.columns.join(', ')})
         SELECT ${source.select.join(', ')}
         FROM read_json_auto(${duckDbStringLiteral(fileName)})${
          source.where === undefined ? '' : ` WHERE ${source.where}`
        }`
      );
    };

    return {
      ensure,
      exec,
      insertFrom: append,
      async replace(source) {
        if (source.from === 'query') {
          // Derives the schema as well as the rows, and leaves no window in
          // which the table is missing.
          await exec(`CREATE OR REPLACE TABLE ${table} AS ${source.sql}`);
          return;
        }
        await ensure();
        await exec(`DELETE FROM ${table}`);
        await append(source, 'all');
      },
      async deleteWhere(where) {
        await exec(`DELETE FROM ${table} WHERE ${where}`);
      },
      async insert({ columns, values }) {
        await exec(
          `INSERT INTO ${table} (${columns.join(', ')}) VALUES (${values.join(
            ', '
          )})`
        );
      },
      async upsert({ where, columns, values }) {
        await ensure();
        // Delete-then-insert rather than `ON CONFLICT DO UPDATE`, because a
        // table that needs this generally cannot carry the key that would give
        // the conflict a target — see the automations table, where DuckDB
        // rejected both a composite conflict target and a delete-and-reinsert
        // of the same key. Uniqueness there is a read-side concern; this only
        // stops the table growing per event.
        await exec(`DELETE FROM ${table} WHERE ${where}`);
        await exec(
          `INSERT INTO ${table} (${columns.join(', ')}) VALUES (${values.join(
            ', '
          )})`
        );
      },
    };
  }
}

/**
 * A table whose rows are grouped by a key, so several owners can share it.
 *
 * Partitioning lives on a subclass rather than as an optional property, so a
 * table without a partition column simply has no partition methods. The
 * alternative — one class that throws when asked to write a partition it
 * cannot address — put a runtime failure where a compile-time one belongs.
 */
export class DuckDbPartitionedTable extends DuckDbTable {
  private readonly partitionColumn: string;

  constructor(params: {
    name: string;
    columns?: readonly string[];
    /** Column every partitioned write scopes by. */
    partitionColumn: string;
  }) {
    super(params);
    this.partitionColumn = params.partitionColumn;
  }

  /** Replaces one partition wholesale, leaving the others alone. */
  async replacePartition(
    db: DuckDbWriteTarget,
    partition: string,
    source: DuckDbTableSource
  ): Promise<void> {
    await this.write(db, async (writer) => {
      await writer.ensure();
      await writer.deleteWhere(this.partitionMatch(partition));
      // Sanitized because a partition key is arbitrary caller input and the
      // slot becomes a path in the wasm filesystem.
      await writer.insertFrom(
        source,
        partition.replaceAll(/[^a-zA-Z0-9_-]/g, '_')
      );
    });
  }

  async deletePartition(
    db: DuckDbWriteTarget,
    partition: string
  ): Promise<void> {
    await this.write(db, (writer) =>
      writer.deleteWhere(this.partitionMatch(partition))
    );
  }

  private partitionMatch(partition: string): string {
    return `${duckDbIdentifier(this.partitionColumn)} = ${duckDbStringLiteral(
      partition
    )}`;
  }
}

/**
 * One queue for every table, keyed by name.
 *
 * Module-level so two tables never wait on each other and two references to
 * the same table always do — a `DuckDbTable` is constructed per render in some
 * places, and a queue held on the instance would serialize nothing.
 */
const duckDbTableWrites = createDuckDbWorkQueue();
